import type { Country, Destination, OptionGroup, OptionKind } from '../model/types';

const KIND_ORDER: readonly OptionKind[] = ['country', 'resort', 'hotel'];

const GROUP_TITLES: Record<'country' | 'resort', { popular: string; other: string }> = {
  country: { popular: 'Популярные направления', other: 'Остальные направления' },
  resort: { popular: 'Популярные курорты', other: 'Остальные курорты' },
};

const NAME_COLLATOR = new Intl.Collator('ru', { sensitivity: 'base' });

function isPopular(item: Destination): boolean {
  return item.type === 'country' || item.type === 'resort' ? item.isPopular : false;
}

function compareWithinKind(a: Destination, b: Destination): number {
  if (a.type === 'country' && b.type === 'country') {
    const positionDiff = comparePosition(a, b);
    if (positionDiff !== 0) {
      return positionDiff;
    }
  }

  return NAME_COLLATOR.compare(a.name, b.name);
}

function comparePosition(a: Country, b: Country): number {
  const positionA = a.position ?? Number.POSITIVE_INFINITY;
  const positionB = b.position ?? Number.POSITIVE_INFINITY;
  return positionA - positionB;
}

function sortWithinKind(items: Destination[], selectedIds: Set<number>): Destination[] {
  return [...items].sort((a, b) => {
    const selectedDiff = Number(selectedIds.has(b.id)) - Number(selectedIds.has(a.id));
    if (selectedDiff !== 0) {
      return selectedDiff;
    }

    const popularDiff = Number(isPopular(b)) - Number(isPopular(a));
    if (popularDiff !== 0) {
      return popularDiff;
    }

    return compareWithinKind(a, b);
  });
}

export interface SortOptionsConfig {
  pinSelected?: boolean;
}

export function sortOptions(
  items: Destination[],
  selectedIds: Set<number> = new Set(),
  config: SortOptionsConfig = {},
): OptionGroup[] {
  if (items.length === 0) {
    return [];
  }

  const groups: OptionGroup[] = [];

  for (const kind of KIND_ORDER) {
    const ofKind = items.filter((item) => item.type === kind);
    if (ofKind.length === 0) {
      continue;
    }

    const sorted = sortWithinKind(ofKind, selectedIds);

    if (kind === 'hotel') {
      groups.push({ title: null, kind, items: sorted });
      continue;
    }

    const pinned = config.pinSelected ? sorted.filter((item) => selectedIds.has(item.id)) : [];
    const rest = pinned.length > 0 ? sorted.filter((item) => !selectedIds.has(item.id)) : sorted;

    if (pinned.length > 0) {
      groups.push({ title: null, kind, items: pinned });
    }

    const popular = rest.filter((item) => isPopular(item));
    const other = rest.filter((item) => !isPopular(item));
    const titles = GROUP_TITLES[kind];

    if (popular.length > 0) {
      groups.push({ title: titles.popular, kind, items: popular });
    }
    if (other.length > 0) {
      groups.push({ title: titles.other, kind, items: other });
    }
  }

  return groups;
}
